<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.groups', 2),
                        href: meta.links.group_index,
                    },
                    {
                        name: $t('messages.student_file'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.show" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.groups', 2)" for="group_ids" />
                        <SlideOverCheckboxInput v-model="form.group_ids" :options="options.groups" />
                        <FormError :value="form.errors.group_ids" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-7">
                    <div class="mt-1">
                        <AppButton @click="slideOverOpen = !slideOverOpen" type="button">
                            {{ $tc('messages.fields', 2) }}
                            <SlideOver :show="slideOverOpen" @close="slideOverOpen = false">
                                <div v-for="optgroup in options.fields" class="mt-3">
                                    <div class="font-semibold flex">
                                        <span>{{ optgroup.label }}</span>

                                        <SelectAllNoneButtons
                                            :max-count="2"
                                            :selected-count="1"
                                            @select-all="selectAllOptgroupOptions(optgroup)"
                                            @select-none="deselectAllOptgroupOptions(optgroup)"
                                            class="flex"
                                        />
                                    </div>
                                    <ul>
                                        <li v-for="option in optgroup.options">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    name="fields[]"
                                                    v-model="form.fields"
                                                    :value="option.value"
                                                    :id="option.value"
                                                    :key="option.value"
                                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                />
                                                {{ option.text }}
                                            </label>
                                        </li>
                                    </ul>
                                </div>

                                <br />
                                <div class="font-semibold text-lg mt-5">{{ $t('messages.school_annotations') }}</div>

                                <div v-for="optgroup in options.customFields" class="mt-3">
                                    <div class="font-semibold flex">
                                        {{ optgroup.label }}
                                        <SelectAllNoneButtons
                                            :max-count="2"
                                            :selected-count="1"
                                            @select-all="selectAllCustomOptgroupOptions(optgroup)"
                                            @select-none="deselectAllCustomOptgroupOptions(optgroup)"
                                            class="flex"
                                        />
                                    </div>
                                    <ul>
                                        <li v-for="option in optgroup.options">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    name="fields[]"
                                                    v-model="form.customFields"
                                                    :value="option.value"
                                                    :id="option.value"
                                                    :key="option.value"
                                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                />
                                                {{ option.text }}
                                            </label>
                                        </li>
                                    </ul>
                                </div>
                                <div class="mt-3 flex space-x-2">
                                    <ButtonPrimary element="button" type="button" @click="filter()">
                                        <IconFilterFilled class="-ml-0.5 mr-2 w-4 h-4" />
                                        {{ $t('messages.filter') }}
                                        <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                                    </ButtonPrimary>
                                </div>
                            </SlideOver>
                        </AppButton>
                        <div class="flex space-x-2 flex-wrap mt-3">
                            <BadgePill v-for="field in selectedFields" :key="field.value">
                                {{ field.text }}
                            </BadgePill>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1 flex space-x-2">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                        <form :action="meta.links.show">
                            <div>
                                <input
                                    type="hidden"
                                    name="fields[]"
                                    :value="field"
                                    :key="field"
                                    v-for="field in filters.fields"
                                />
                                <input
                                    type="hidden"
                                    name="customFields[]"
                                    :value="field"
                                    :key="field"
                                    v-for="field in filters.customFields"
                                />
                                <input
                                    type="hidden"
                                    name="group_ids[]"
                                    :value="group_id"
                                    :key="group_id"
                                    v-for="group_id in filters.group_ids"
                                />
                            </div>
                            <ButtonWhite element="button" name="wants_pdf" type="submit">
                                {{ $t('messages.pdf') }}
                            </ButtonWhite>
                        </form>
                    </div>
                </div>
            </div>
        </form>
        <div>
            <FormError :value="form.errors.fields" class="mt-2" />
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.group') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    v-for="fieldName in fieldNames"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ fieldName.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(student, index) in students"
                            >
                                <td class="max-w-sm px-6 py-4 text-sm leading-5 font-medium text-gray-900">
                                    {{ student.group_name }}
                                </td>
                                <td class="max-w-sm px-6 py-4 text-sm leading-5 font-medium text-gray-900">
                                    <a :href="student.links.show">
                                        {{ student.full_name }}
                                    </a>
                                </td>
                                <td
                                    v-for="field in student.values"
                                    class="max-w-sm px-6 py-4 text-sm leading-5 font-medium text-gray-900"
                                >
                                    <span v-if="field.value === '::no_info::'" class="text-gray-400">
                                        {{ $t('messages.no_info') }}
                                    </span>
                                    <span v-else :class="field.is_null ? 'text-gray-400' : ''">
                                        {{ field.value }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import ButtonPrimary from '@/components/ButtonPrimary.vue';
import ButtonWhite from '@/components/ButtonWhite.vue';
import AppButton from '@/components/AppButton.vue';
import BadgePill from '@/components/BadgePill.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';
import { pullAll, union } from 'lodash';
import SlideOver from '@/components/SlideOver.vue';
import { IconFilterFilled } from '@tabler/icons-vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        IconFilterFilled,
        SlideOver,
        PanelPage,
        FormError,
        ButtonPrimary,
        ButtonWhite,
        AppButton,
        BadgePill,
        FormLabel,
        SlideOverCheckboxInput,
        SelectAllNoneButtons,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'options', 'students', 'filters', 'fieldNames'],
    data() {
        return {
            form: this.$inertia.form({
                fields: this.filters.fields,
                customFields: this.filters.customFields.map((f) => +f),
                group_ids: this.filters.group_ids,
            }),
            slideOverOpen: false,
        };
    },
    computed: {
        selectedFields: function () {
            return this.fieldNames.filter((f) => {
                if (this.form.customFields.includes(`${f.value}`) || this.form.customFields.includes(f.value)) {
                    return true;
                }
                return this.form.fields.includes(f.value);
            });
        },
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        selectAllOptgroupOptions(optgroup) {
            let ids = optgroup.options.map((o) => o.value);
            this.form.fields = union(this.form.fields, ids);
        },
        deselectAllOptgroupOptions(optgroup) {
            let ids = optgroup.options.map((o) => o.value);
            pullAll(this.form.fields, ids);
        },
        selectAllCustomOptgroupOptions(optgroup) {
            let ids = optgroup.options.map((o) => o.value);
            this.form.customFields = union(this.form.customFields, ids);
        },
        deselectAllCustomOptgroupOptions(optgroup) {
            let ids = optgroup.options.map((o) => o.value);
            pullAll(this.form.customFields, ids);
        },
    },
};
</script>
